/**
 * Handles marquee (rectangular lasso) selection for selecting multiple canvas elements.
 * Draws a selection rectangle as the user drags, then returns all intersecting elements.
 */
export default class MarqueeSelection {
  /**
   * @param {Object} canvas - The canvas controller instance
   */
  constructor(canvas) {
    this.canvas = canvas;
    this.canvasElement = canvas.canvasElement;
    this.isSelecting = false;
    this.startX = 0;
    this.startY = 0;
    this.marqueeElement = null;

    this._createMarqueeElement();
  }

  _createMarqueeElement() {
    this.marqueeElement = document.createElement('div');
    this.marqueeElement.className = 'marquee-selection';
    this.marqueeElement.style.display = 'none';
    this.canvasElement.appendChild(this.marqueeElement);
  }

  /**
   * Initiates a marquee selection. Only starts if clicking directly on the canvas background.
   *
   * @param {MouseEvent} e - The mousedown event
   * @returns {boolean} Whether the selection was started
   */
  startSelection(e) {
    // Only start marquee if clicking directly on canvas (not on an element)
    if (e.target !== this.canvasElement) return false;

    const rect = this.canvasElement.getBoundingClientRect();
    this.startX = e.clientX - rect.left;
    this.startY = e.clientY - rect.top;

    this.isSelecting = true;
    this.marqueeElement.style.display = 'block';
    this.marqueeElement.style.left = this.startX + 'px';
    this.marqueeElement.style.top = this.startY + 'px';
    this.marqueeElement.style.width = '0px';
    this.marqueeElement.style.height = '0px';

    return true;
  }

  /**
   * Updates the marquee rectangle as the user drags. Handles dragging in any direction.
   * @param {MouseEvent} e - The mousemove event
   */
  updateSelection(e) {
    if (!this.isSelecting) return;

    const rect = this.canvasElement.getBoundingClientRect();
    const currentX = e.clientX - rect.left;
    const currentY = e.clientY - rect.top;

    // Calculate marquee bounds (handle negative drag directions)
    const left = Math.min(this.startX, currentX);
    const top = Math.min(this.startY, currentY);
    const width = Math.abs(currentX - this.startX);
    const height = Math.abs(currentY - this.startY);

    this.marqueeElement.style.left = left + 'px';
    this.marqueeElement.style.top = top + 'px';
    this.marqueeElement.style.width = width + 'px';
    this.marqueeElement.style.height = height + 'px';
  }

  /**
   * Ends the marquee selection and returns all elements that intersect with the marquee.
   * @returns {HTMLElement[]} Elements that intersect with the marquee rectangle
   */
  endSelection() {
    if (!this.isSelecting) return [];

    this.isSelecting = false;

    // Get marquee bounds
    const marqueeBounds = {
      left: parseInt(this.marqueeElement.style.left),
      top: parseInt(this.marqueeElement.style.top),
      right: parseInt(this.marqueeElement.style.left) + parseInt(this.marqueeElement.style.width),
      bottom: parseInt(this.marqueeElement.style.top) + parseInt(this.marqueeElement.style.height)
    };

    // Hide marquee
    this.marqueeElement.style.display = 'none';

    // Find all elements that intersect with the marquee
    const elements = this.canvas.getElements();
    const selectedElements = [];

    elements.forEach(element => {
      const elementBounds = {
        left: parseInt(element.style.left),
        top: parseInt(element.style.top),
        right: parseInt(element.style.left) + element.offsetWidth,
        bottom: parseInt(element.style.top) + element.offsetHeight
      };

      if (this._boundsIntersect(marqueeBounds, elementBounds)) {
        selectedElements.push(element);
      }
    });

    return selectedElements;
  }

  isActive() {
    return this.isSelecting;
  }

  _boundsIntersect(a, b) {
    return !(
      a.right < b.left ||
      a.left > b.right ||
      a.bottom < b.top ||
      a.top > b.bottom
    );
  }
}
